/**
 * The three states, as this feature needs them — built ON TOP OF web/ui's shared components, not
 * beside them.
 *
 * `@moonx/ui` owns the PRESENTATION of loading and error: the polite live region a skeleton cannot
 * make for itself, the `role="alert"` Mantine's `Alert` does not set, the ragged last line, the
 * retry that is actually wired. None of that is re-implemented here.
 *
 * WHAT THIS FILE ADDS is the one thing that cannot live in a shared package: THE WORDING OF A
 * REFUSAL. `error_code` is the contract and `title`/`detail` are explicitly not places a client
 * branches, so turning a refusal into a sentence is a per-surface job — a 503 on the application
 * flow means "your work is safe, you are still signed in", and that sentence has no business in a
 * generic component. `humanMessage` in ../api/problem is the only translator, and nothing here
 * ever puts a machine code or a stack trace on screen.
 *
 * `EmptyState` is Mantine 9's own; the component inventory says in terms not to hand-roll one.
 */
import { Button, EmptyState, Group, Paper, Text } from '@mantine/core';
import { ErrorState as UiErrorState, LoadingState as UiLoadingState } from '@moonx/ui';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { humanMessage, isRateLimited, isSessionGone, isUpstreamUnavailable } from '../api/problem';
import { defined } from './defined';

export interface LoadingStateProps {
  /** What is loading, in the person's words. Announced politely; a skeleton says nothing alone. */
  readonly label: string;
  /** Shape it like the content it stands in for. */
  readonly lines?: number;
}

export function LoadingState({ label, lines = 3 }: LoadingStateProps): ReactNode {
  return <UiLoadingState label={label} lines={lines} />;
}

export interface ErrorStateProps {
  readonly error: unknown;
  /** The sentence to fall back to when the refusal is one this feature does not know. */
  readonly fallback: string;
  readonly onRetry?: (() => void) | undefined;
  readonly title?: string | undefined;
}

export function ErrorState({ error, fallback, onRetry, title }: ErrorStateProps): ReactNode {
  const { t } = useTranslation('application');
  const sessionGone = isSessionGone(error);
  const upstream = isUpstreamUnavailable(error);
  const throttled = isRateLimited(error);

  const heading =
    title ??
    (sessionGone
      ? t('states.error.sessionEnded')
      : upstream
        ? t('states.error.unavailable')
        : throttled
          ? t('states.error.oneMoment')
          : t('states.error.somethingWentWrong'));

  return (
    <UiErrorState
      title={heading}
      retryLabel={sessionGone ? t('states.error.reloadAndSignIn') : t('states.tryAgain')}
      {...defined({ onRetry })}
    >
      <Text size="sm">{humanMessage(error, fallback, undefined, t)}</Text>
      {upstream ? (
        // Said out loud because the opposite is what a person assumes when a page fails mid-form.
        <Text size="xs" c="dimmed" mt={4}>
          {t('states.error.nothingLost')}
        </Text>
      ) : null}
    </UiErrorState>
  );
}

export interface EmptyPanelProps {
  readonly title: string;
  /** Why it is empty AND what goes here. An empty state that says nothing is a bug, not a blank. */
  readonly description: string;
  /** Exactly one action, or none. Never two. */
  readonly action?: ReactNode;
}

export function EmptyPanel({ title, description, action }: EmptyPanelProps): ReactNode {
  return (
    <Paper withBorder p="lg" radius="md">
      {/* `align="left"` rather than Mantine's centred default: this sits inside a form, where a
          centred block reads as a dead end rather than as the next thing to do. */}
      <EmptyState title={title} description={description} align="left">
        {action ? <EmptyState.Actions>{action}</EmptyState.Actions> : null}
      </EmptyState>
    </Paper>
  );
}

/**
 * A small failure that must NOT take the page down: an optional list that would not load. It is
 * rendered where the list is, with its own retry, because a page that refuses to draw because a
 * five-point optional list failed has confused its own priorities.
 */
export function InlineFailure({
  error,
  fallback,
  onRetry,
}: {
  readonly error: unknown;
  readonly fallback: string;
  readonly onRetry: () => void;
}): ReactNode {
  const { t } = useTranslation('application');
  return (
    <Group gap="sm" role="alert" wrap="nowrap" align="flex-start">
      <Text size="sm" style={{ color: 'oklch(var(--mx-danger))' }}>
        {humanMessage(error, fallback, undefined, t)}
      </Text>
      <Button size="compact-xs" variant="subtle" onClick={onRetry}>
        {t('states.tryAgain')}
      </Button>
    </Group>
  );
}
